/**
 * Generic 2D ground-plane geometry. Deliberately has zero knowledge of golf
 * (no "fairway", no "hole") — this lives in utils/ rather than course/ so
 * Terrain (an engine-adjacent, course-agnostic module) can depend on it too.
 *
 * x/z map directly to Three.js world-space X/Z (ground plane); Y (height)
 * is handled separately by Terrain's height sampling.
 */

export interface Vec2 {
  x: number;
  z: number;
}

export type Region =
  { kind: 'polygon'; points: Vec2[] } | { kind: 'circle'; center: Vec2; radius: number };

export function isPointInRegion(point: Vec2, region: Region): boolean {
  if (region.kind === 'circle') {
    const dx = point.x - region.center.x;
    const dz = point.z - region.center.z;
    return dx * dx + dz * dz <= region.radius * region.radius;
  }
  return isPointInPolygon(point, region.points);
}

/** Standard ray-casting point-in-polygon test. Polygon need not be convex. */
function isPointInPolygon(point: Vec2, points: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const pi = points[i];
    const pj = points[j];
    const intersects =
      pi.z > point.z !== pj.z > point.z &&
      point.x < ((pj.x - pi.x) * (point.z - pi.z)) / (pj.z - pi.z) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

export interface RegionBounds {
  centerX: number;
  centerZ: number;
  width: number;
  depth: number;
}

export function computeRegionBounds(region: Region): RegionBounds {
  const points: Vec2[] =
    region.kind === 'circle'
      ? [
          { x: region.center.x - region.radius, z: region.center.z - region.radius },
          { x: region.center.x + region.radius, z: region.center.z + region.radius },
        ]
      : region.points;

  const xs = points.map((p) => p.x);
  const zs = points.map((p) => p.z);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minZ = Math.min(...zs);
  const maxZ = Math.max(...zs);

  return {
    centerX: (minX + maxX) / 2,
    centerZ: (minZ + maxZ) / 2,
    width: maxX - minX,
    depth: maxZ - minZ,
  };
}

export function cloneRegion(region: Region): Region {
  return region.kind === 'circle'
    ? { kind: 'circle', center: { ...region.center }, radius: region.radius }
    : { kind: 'polygon', points: region.points.map((p) => ({ ...p })) };
}

export function resizeCircle(region: Region, radius: number): Region {
  return region.kind === 'circle'
    ? ({ ...cloneRegion(region), radius: Math.max(1, radius) } as Region)
    : cloneRegion(region);
}

export function movePolygonVertex(region: Region, index: number, point: Vec2): Region {
  if (region.kind !== 'polygon') return cloneRegion(region);
  return {
    kind: 'polygon',
    points: region.points.map((p, i) => (i === index ? { ...point } : { ...p })),
  };
}

export function addPolygonVertex(region: Region, index: number, point: Vec2): Region {
  if (region.kind !== 'polygon') return cloneRegion(region);
  const points = region.points.map((p) => ({ ...p }));
  points.splice(Math.max(0, Math.min(points.length, index)), 0, { ...point });
  return { kind: 'polygon', points };
}

export function removePolygonVertex(region: Region, index: number): Region {
  if (region.kind !== 'polygon' || region.points.length <= 3) return cloneRegion(region);
  return {
    kind: 'polygon',
    points: region.points.filter((_, i) => i !== index).map((p) => ({ ...p })),
  };
}

export interface FairwayControlPoint extends Vec2 {
  width: number;
}
export function compileFairwayCenterline(points: FairwayControlPoint[]): Region {
  if (points.length < 2) return { kind: 'polygon', points: [] };
  const left: Vec2[] = [];
  const right: Vec2[] = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(points.length - 1, i + 1)];
    const dx = next.x - prev.x;
    const dz = next.z - prev.z;
    const len = Math.hypot(dx, dz) || 1;
    const nx = -dz / len;
    const nz = dx / len;
    const half = Math.max(2, points[i].width / 2);
    left.push({ x: points[i].x + nx * half, z: points[i].z + nz * half });
    right.unshift({ x: points[i].x - nx * half, z: points[i].z - nz * half });
  }
  return { kind: 'polygon', points: [...left, ...right] };
}

export interface RegionHitCandidate<T = string> {
  id: T;
  region: Region;
  priority?: number;
}

export interface RegionHit<T = string> {
  id: T;
  region: Region;
  inside: boolean;
  edgeDistance: number;
  vertexIndex: number | null;
  vertexDistance: number;
  area: number;
  score: number;
}

export interface RegionHitOptions {
  edgeTolerance?: number;
  vertexTolerance?: number;
}

export function regionArea(region: Region): number {
  if (region.kind === 'circle') return Math.PI * region.radius * region.radius;
  return Math.abs(polygonSignedArea(region.points));
}

export function distanceToRegionEdge(point: Vec2, region: Region): number {
  if (region.kind === 'circle') {
    return Math.abs(
      Math.hypot(point.x - region.center.x, point.z - region.center.z) - region.radius,
    );
  }
  if (region.points.length === 0) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < region.points.length; i++) {
    best = Math.min(
      best,
      distanceToSegment(point, region.points[i], region.points[(i + 1) % region.points.length]),
    );
  }
  return best;
}

export function nearestRegionVertex(
  point: Vec2,
  region: Region,
): { index: number | null; distance: number } {
  if (region.kind === 'circle') return { index: null, distance: Number.POSITIVE_INFINITY };
  let index: number | null = null;
  let distance = Number.POSITIVE_INFINITY;
  region.points.forEach((candidate, i) => {
    const d = Math.hypot(point.x - candidate.x, point.z - candidate.z);
    if (d < distance) {
      distance = d;
      index = i;
    }
  });
  return { index, distance };
}

export function hitTestRegions<T>(
  point: Vec2,
  candidates: readonly RegionHitCandidate<T>[],
  options: RegionHitOptions = {},
): RegionHit<T>[] {
  const edgeTolerance = options.edgeTolerance ?? 3;
  const vertexTolerance = options.vertexTolerance ?? 2.25;
  return candidates
    .map((candidate) => {
      const inside = isPointInRegion(point, candidate.region);
      const edgeDistance = distanceToRegionEdge(point, candidate.region);
      const vertex = nearestRegionVertex(point, candidate.region);
      const area = regionArea(candidate.region);
      const vertexIntent = vertex.distance <= vertexTolerance ? -10_000 + vertex.distance : 0;
      const edgeIntent = edgeDistance <= edgeTolerance ? -5_000 + edgeDistance : 0;
      const insideIntent = inside ? -1_000 : 0;
      const specificity = Math.sqrt(Math.max(1, area)) * 0.001;
      const priority = candidate.priority ?? 0;
      return {
        id: candidate.id,
        region: candidate.region,
        inside,
        edgeDistance,
        vertexIndex: vertex.index,
        vertexDistance: vertex.distance,
        area,
        score: vertexIntent + edgeIntent + insideIntent + specificity - priority,
      } satisfies RegionHit<T>;
    })
    .filter(
      (hit) =>
        hit.inside || hit.edgeDistance <= edgeTolerance || hit.vertexDistance <= vertexTolerance,
    )
    .sort((a, b) => a.score - b.score || a.area - b.area);
}

function polygonSignedArea(points: readonly Vec2[]): number {
  return (
    points.reduce((sum, p, i) => {
      const q = points[(i + 1) % points.length];
      return sum + p.x * q.z - q.x * p.z;
    }, 0) / 2
  );
}

function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const lenSq = dx * dx + dz * dz || 1;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq));
  return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

/** Area-weighted polygon centroid; independent of vertex ordering start point. */
export function polygonCentroid(points: readonly Vec2[]): Vec2 {
  if (points.length === 0) return { x: 0, z: 0 };
  let twiceArea = 0;
  let cx = 0;
  let cz = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const cross = a.x * b.z - b.x * a.z;
    twiceArea += cross;
    cx += (a.x + b.x) * cross;
    cz += (a.z + b.z) * cross;
  }
  if (Math.abs(twiceArea) < 0.000001) {
    const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, z: acc.z + p.z }), { x: 0, z: 0 });
    return { x: sum.x / points.length, z: sum.z / points.length };
  }
  return { x: cx / (3 * twiceArea), z: cz / (3 * twiceArea) };
}
